<template>
    <v-dialog :value="value" persistent max-width="780px">
        <v-card v-if="record">
            <v-card-title>
                {{ $t('show_link') }}
            </v-card-title>
            <v-card-text>
                <v-container fluid>
                    <v-row dense>
                        <v-col cols="9">
                            <h4>{{ record.title || `${record.first_name} ${record.surname}` }}</h4>
                        </v-col>
                        <v-col cols="3" class="text-right">
                            <PlainBtn @click="$emit('edit-address', record)">
                                <KpIcon icon-cls="arrowrightup" color="primary" />
                                <span style="color: var(--primary-color)">{{ $t('edit_contact') }}</span>
                            </PlainBtn>
                        </v-col>
                    </v-row>
                    <v-row v-if="record.addressContact" dense>
                        <v-col>
                            <LabelField :label="$t('func_info')">
                                <v-textarea
                                    :value="record.addressContact ? record.addressContact.relation_function : null"
                                    dense
                                    outlined
                                    rows="3"
                                    placeholder="Funktion / Verknüpfungsinformation, z.B. 'Veranstaltungsleiterin'"
                                    @input="
                                        record.addressContact
                                            ? record.addressContact.set('relation_function', $event)
                                            : null
                                    "
                                ></v-textarea>
                            </LabelField>
                        </v-col>
                    </v-row>
                    <v-row class="mt-5">
                        <v-col>
                            <v-simple-table>
                                <tbody>
                                    <tr v-if="record.email">
                                        <td style="width: 150px">{{ $t('email') }}</td>
                                        <td>
                                            <a :href="`mailto:${record.email}`" rel="noreferrer noopener">{{
                                                record.email
                                            }}</a>
                                        </td>
                                    </tr>
                                    <tr v-if="record.tel">
                                        <td style="width: 150px">{{ $t('tel_1') }}</td>
                                        <td>
                                            <a :href="`tel:${record.tel}`" rel="noreferrer noopener">{{
                                                record.tel
                                            }}</a>
                                        </td>
                                    </tr>
                                    <tr v-if="record.mobile">
                                        <td style="width: 150px">{{ $t('tel_2') }}</td>
                                        <td>
                                            <a :href="`tel:${record.mobile}`" rel="noreferrer noopener">{{
                                                record.mobile
                                            }}</a>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style="width: 150px">{{ $t('publication') }}</td>
                                        <td>
                                            <v-checkbox
                                                class="mt-0 pt-0"
                                                :input-value="
                                                    record.addressContact
                                                        ? record.addressContact.publish_frontend
                                                        : null
                                                "
                                                hide-details="auto"
                                                @change="
                                                    record.addressContact
                                                        ? record.addressContact.set('publish_frontend', $event)
                                                        : null
                                                "
                                            >
                                                <template #label>
                                                    <KpIcon left icon-cls="published" color="grey" />
                                                    <span v-html="$t('frontend_publish_contact')"></span>
                                                </template>
                                            </v-checkbox>
                                        </td>
                                    </tr>
                                    <tr>
                                        <td style="width: 150px">{{ $t('main_contact') }}</td>
                                        <td class="d-flex">
                                            <v-checkbox
                                                class="mt-0 pt-0"
                                                :input-value="
                                                    record.addressContact ? record.addressContact.is_main_contact : null
                                                "
                                                hide-details="auto"
                                                :readonly="!userIsStaff()"
                                                @change="
                                                    record.addressContact
                                                        ? record.addressContact.set('is_main_contact', $event)
                                                        : null
                                                "
                                            >
                                                <template #label>
                                                    <KpIcon left icon-cls="star" color="grey" />
                                                    <span v-html="$t('main_contact_msgs')"></span>
                                                </template>
                                            </v-checkbox>
                                            <KpTooltip>
                                                <template #activator="{ on, attrs }">
                                                    <i
                                                        v-bind="attrs"
                                                        class="ic ic-16 ic-col-light-blue ic-support ml-1"
                                                        v-on="on"
                                                    ></i>
                                                </template>
                                                <div style="max-width: 350px">{{$t('main_contact_msgs_info')}}</div>
                                            </KpTooltip>
                                        </td>
                                    </tr>
                                </tbody>
                            </v-simple-table>
                        </v-col>
                    </v-row>
                </v-container>
            </v-card-text>

            <v-card-actions>
                <v-spacer />
                <PlainBtn @click="$emit('input', false)">{{ $t('schliessen') }}</PlainBtn>
                <ActionBtn @click="onSaveBtn" @loading="loading">
                    <KpIcon icon-cls="pencil" color="white" />
                    <span>{{ $t('save') }}</span>
                </ActionBtn>
                <ActionBtn @click="$emit('delete-link', record)">
                    <KpIcon icon-cls="link" color="white" />
                    <span>{{ $t('delete_link') }}</span>
                </ActionBtn>
            </v-card-actions>
        </v-card>
        <v-skeleton-loader v-else type="card" />
    </v-dialog>
</template>

<script>
import { errToStr } from '@/helpers/tools';
import { Address } from '@/models/Address';
import { userIsStaff } from '@/helpers/auth';

export default {
    name: 'ShowAddressContactDlg',
    props: {
        value: {
            type: Boolean,
            default: false,
        },
        record: {
            type: Address,
            default: null,
        },
    },
    data() {
        return {
            loading: false,
        };
    },
    methods: {
        userIsStaff,

        async onSaveBtn() {
            // we only save the assigned address contact, IF available:
            if (this.record && this.record.addressContact) {
                if (this.record.addressContact.changed()) {
                    this.loading = true;
                    try {
                        await this.record.addressContact.save();
                    } catch (e) {
                        this.$toast({ message: errToStr(e), color: 'error' });
                    } finally {
                        this.loading = false;
                    }
                }
            }

            this.$emit('saved', this.record);
        },
    },
};
</script>
